import { faCube, faImage } from "@fortawesome/pro-solid-svg-icons";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useState } from "react";

import { Icon } from "@/components/ui-v2/Icon/Icon";
import { AudioPreviewTile } from "@/components/ui-v2/Tiles/AudioPreviewTile/AudioPreviewTile";
import { TileObjectTypeIndicator } from "@/components/ui-v2/Tiles/common/TileObjectTypeIndicator";
import { DirectoryTile } from "@/components/ui-v2/Tiles/DirectoryTile/DirectoryTile";
import { ImagePreviewTile } from "@/components/ui-v2/Tiles/ImagePreviewTile/ImagePreviewTile";
import { ModelTile } from "@/components/ui-v2/Tiles/ModelTile/ModelTile";
import { SafeTensorsTile } from "@/components/ui-v2/Tiles/SafetensorsTile/SafetensorsTile";
import { TileBase } from "@/components/ui-v2/Tiles/TileBase/TileBase";
import { VideoPreviewTile } from "@/components/ui-v2/Tiles/VideoPreviewTile/VideoPreviewTile";
import { getObjectMetadata } from "@/utils/libraryObjectUtils";
import { getSrcSet } from "@/utils/thumbnails";

import { ObjectType, Thumbnails } from "../../types";
import { useDrive } from "./DriveContext";
import { DriveGridItemFooter } from "./DriveGridItemFooter";

const StyledIcon = styled(Icon)({
  fontSize: "3em",
});

type DriveGridItemProps = {
  text: string;
  subText?: string;
  isSelected: boolean;
  isDragging: boolean;
  onContextMenu: (event: React.MouseEvent) => void;
  src?: string;
  previewUrl?: string;
  width?: number;
  thumbnails?: Thumbnails;
  fileType?: ObjectType;
  onFileClick: (event: React.MouseEvent) => void;
  onFileDoubleClick: (event: React.MouseEvent) => void;
  id?: string;
};

export const DriveGridItem = (props: DriveGridItemProps) => {
  const {
    text,
    subText,
    isSelected,
    isDragging,
    onContextMenu,
    src,
    previewUrl,
    width,
    thumbnails,
    fileType,
    onFileClick,
    onFileDoubleClick,
  } = props;
  const [isError, setIsError] = useState(false);

  const { actionsDisabled } = useDrive();

  const { icon } = getObjectMetadata(fileType);

  const handleAction = (event: React.MouseEvent, action: string) => {
    switch (action) {
      case "SELECT":
        onFileClick(event);
        break;
      case "DOUBLE_CLICK":
        onFileDoubleClick(event);
        break;
      case "OPEN_CONTEXT_MENU":
        if (!actionsDisabled) {
          onContextMenu(event);
        }
        break;
      default:
        break;
    }
  };

  let tile: React.ReactNode | null = null;

  const commonProps = {
    isSelected,
    isSelectable: true,
    onClick: (event: React.MouseEvent) => handleAction(event, "SELECT"),
    onDoubleClick: (event: React.MouseEvent) =>
      handleAction(event, "DOUBLE_CLICK"),
    onContextMenu: (event: React.MouseEvent) =>
      handleAction(event, "OPEN_CONTEXT_MENU"),
    slots: {
      topLeftEl: TileObjectTypeIndicator,
    },
    slotProps: {
      topLeftEl: {
        objectType: fileType,
      },
    },
  };

  if (fileType === "image" && src) {
    const srcSet = getSrcSet({ thumbnails, metadata: { width }, url: src });
    tile = (
      <ImagePreviewTile
        {...commonProps}
        src={src}
        alt={text}
        imgProps={{ srcSet, sizes: "auto", loading: "lazy" }}
      />
    );
  } else if (fileType === "psd" && previewUrl) {
    const srcSet = getSrcSet({
      thumbnails,
      metadata: { width },
      url: previewUrl,
    });
    tile = (
      <ImagePreviewTile
        {...commonProps}
        src={previewUrl}
        alt={text}
        imgProps={{ srcSet, sizes: "auto", loading: "lazy" }}
      />
    );
  } else if (fileType === "video" && src) {
    tile = <VideoPreviewTile {...commonProps} src={src} />;
  } else if (fileType === "audio" && src) {
    tile = (
      <AudioPreviewTile {...commonProps} url={src} onAction={handleAction} />
    );
  } else if (fileType === "mesh") {
    const meshThumbnail = thumbnails?.small?.url;
    tile =
      isError || !src || !meshThumbnail ? (
        <TileBase {...commonProps}>
          <StyledIcon icon={faCube} />
        </TileBase>
      ) : (
        <ImagePreviewTile
          {...commonProps}
          src={meshThumbnail}
          alt={text}
          imgProps={{ onError: () => setIsError(true) }}
        />
      );
  } else if (fileType === "canvas") {
    tile =
      isError || !src ? (
        <TileBase {...commonProps}>
          <StyledIcon icon={faImage} />
        </TileBase>
      ) : (
        <ImagePreviewTile
          {...commonProps}
          src={src}
          alt={text}
          imgProps={{ onError: () => setIsError(true) }}
        />
      );
  } else if (fileType === "style") {
    tile = <ModelTile {...commonProps} />;
  } else if (fileType === "safetensors") {
    tile = <SafeTensorsTile {...commonProps} />;
  } else if (fileType === "directory") {
    tile = <DirectoryTile {...commonProps} slots={{}} />;
  } else {
    tile = <TileBase {...commonProps}>{icon}</TileBase>;
  }

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        pointerEvents: isDragging ? "none" : "auto",
        gap: "4px",
      }}
    >
      {tile}
      <DriveGridItemFooter
        text={text}
        subText={subText}
        actionsDisabled={actionsDisabled}
        onContextMenu={onContextMenu}
      />
    </Box>
  );
};
